import { Drawer, Loading } from '@/components';
import { useMessage } from '@/providers';
import dayjs from 'dayjs';
import type { FC } from 'react';
import { useTranslation } from 'react-i18next';
import invariant from 'tiny-invariant';
import type { z } from 'zod';
import {
  useCreateMinistry,
  useMinistry,
  useUpdateMinistry,
} from '../../hooks/ministry';
import type { UpsertMinistryRequestSchema } from '../../models/ministry';
import { useDrawerState } from '../DrawerStateProvider';
import { MinistryForm } from './MinistryForm';

export const MinistryFormDrawer: FC = () => {
  const { t } = useTranslation();
  const { openMessage } = useMessage();
  const { drawerState, setDrawerState } = useDrawerState();

  const { mutateAsync: createMutateAsync } = useCreateMinistry();
  const { mutateAsync: updateMutateAsync } = useUpdateMinistry();

  const isOpen = drawerState.open && drawerState.type === 'ministry';
  const ministryId = isOpen && drawerState.isEditing ? drawerState.id : '';

  const { data: ministry, isPending } = useMinistry(ministryId);

  const handleClose = () => {
    setDrawerState({ open: false });
  };

  return (
    <Drawer open={isOpen} onClose={handleClose} destroyOnClose>
      {ministryId && isPending ? (
        <Loading />
      ) : (
        <MinistryForm
          initialValues={
            ministry
              ? {
                  code: ministry.code,
                  name: ministry.name,
                  period: [
                    ministry.startDate ? dayjs(ministry.startDate) : null,
                    ministry.endDate ? dayjs(ministry.endDate) : null,
                  ],
                }
              : undefined
          }
          onSubmit={async (_values) => {
            invariant(isOpen, t('unreachable-code'));

            const startDate = _values.period?.at(0)?.format('YYYY-MM-DD');
            const endDate = _values.period?.at(1)?.format('YYYY-MM-DD');

            const values: z.infer<typeof UpsertMinistryRequestSchema> = {
              ..._values,
              startDate,
              endDate,
            };

            drawerState.isEditing
              ? await updateMutateAsync({ ...values, id: drawerState.id })
              : await createMutateAsync(values);

            await Promise.all([
              openMessage(
                'success',
                drawerState.isEditing
                  ? t('message-update-completed')
                  : t('message-create-completed'),
              ),
              (async () => {
                handleClose();
              })(),
            ]);
          }}
        />
      )}
    </Drawer>
  );
};
